import * as ToggleGroup from "@radix-ui/react-toggle-group";
import { HexColorPicker } from "react-colorful";
import {
  Alert,
  Button,
  Divider,
  Dropdown,
  Flex,
  Form,
  Input,
  message,
  Modal,
  Popover,
  Radio,
  Select,
  Space,
  Switch,
  Tooltip,
  Typography,
  Segmented,
} from "antd";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { canvasBackgrounds } from "../backgrounds/canvasBackgrounds";
import TeleprompterModal from "../components/Teleprompter";
import useLayoutManager from "../context/useLayoutManager";
import useRecorderContext from "../context/useRecorderContext";
import { useAtom } from "jotai";
import { RecordingQuality, isRatioTransitioningAtom, canvasTransitionSnapshotAtom } from "../store/canvasStore";
import { store } from "../store/canvasStoreInstance";
import { isTeleprompterOpenAtom, currentSceneAtom, sceneListAtom, activeProjectAtom, updateSceneInListAtom } from "../atoms/sceneAtoms";
import useNoiseControlledStream from "../hooks/useNoiseControlledStream";
import useRecordingStarter from "../hooks/useRecordingStarter";
import useStreamVideo from "../hooks/useStreamVideo";
import useAutoLayoutPreset from "../hooks/useAutoLayoutPreset";
import { usePipWindow } from "../hooks/usePipWindow";
import { useResponsiveToolbarModes } from "../hooks/useResponsiveToolbarModes";
import { useUserStore } from "../hooks/useUserStore";
import { detectBrowser } from "../utils/browserCompatibility";
import { registerStream, unregisterStream } from "../utils/activeStreams";
import { useFeatureAccess } from "../hooks/useFeatureAccess";
import { useRecordingLimit } from "../hooks/useRecordingLimit";
import { useDeviceCapabilityQuality, getQualityDropdownItems } from "../hooks/useDeviceCapabilityQuality";
import { UpgradePrompt } from "./overlays/UpgradePrompt";
import { PresetFn, Preset_WebcamFull, Preset_ScreenFull, landscapePresets, portraitPresets, squarePresets } from "../presets/layoutPresets";
import { DeviceCapabilityHint } from "./hints";
import {
  landscapePresetsWithPreview,
  portraitPresetsWithPreview,
  squarePresetsWithPreview,
  getPresetByKey,
} from "../presets/layoutPresetsPreview";
import {
  getMicrophoneStream,
  getRawMicrophoneStream,
  getScreenStream,
  getWebcamStream,
} from "../utils/mediaStreams";
import { getElapsedTimeFormatted } from "../utils/recordingTime";

//Styles
import "./recordingToolbar.scss";

import {
  ChevronDown,
  Crown,
  Image,
  Lock,
  Mic,
  MicOff,
  NotepadText,
  PanelsTopLeft,
  PictureInPicture,
  Pipette,
  Ratio,
  ScreenShare,
  ScreenShareOff,
  Settings,
  Pause,
  Play,
  Trash2,
  Video,
  VideoOff,
  Upload,
  X,
  Clock,
  RotateCcw,
} from "lucide-react";
import logger from "../utils/logger";
import VolumeProgress from "./VolumeProgress";
import { SceneService } from "../services/sceneService";
import { useSearchParams } from "react-router-dom";

const { Title, Text } = Typography;

interface RecordingToolbarProps {
  layerRef: React.RefObject<any>;
}

export interface RecordingToolbarHandle {
  checkPresetStreamRequirements: (presetKey: string) => boolean;
  stopWebcam: () => void;
  stopScreenShare: () => void;
}

const RecordingToolbarBottom = forwardRef<RecordingToolbarHandle, RecordingToolbarProps>(({
  layerRef,
}, ref) => {
  const {
    canvasWidth,
    canvasHeight,
    isRecording,
    isPaused,
    isUploading,
    stopRecording,
    pauseRecording,
    resumeRecording,
    deleteRecording,
    setScreenStream,
    screenStream,
    setWebcamStream,
    webcamStream,
    microphoneStream,
    setMicrophoneStream,
    rawMicrophoneStream,
    setRawMicrophoneStream,
    isSystemAudioEnabled,
    setIsSystemAudioEnabled,
    setBackgroundImage,
    setBackgroundColor,
    customBackgroundImages,
    addCustomBackgroundImage,
    removeCustomBackgroundImage,
    setIntendedBackgroundImage,
    updateBackgroundKey,
    setCanvasRatio,
    canvasRatio,
    selectedPreset,
    setSelectedPreset,
    isManualPreset,
    setIsManualPreset,
    micDevices,
    webcamDevices,
    roundedWebcam,
    setRoundedWebcam,
    mirroredWebcam,
    avatarWebcam,
    enableCountdown,
    setEnableCountdown,
    showCountdown,
    stopSingleStreamRecording,
    toggleSeparateStream,
    useSeparateStreamsMode,
    initialStreamLayout,
    recordingStartTime,
    showGrid,
    setShowGrid,
    setShowCountdown,
    hasManualModifications,
    setHasManualModifications,
    setInitialStreamLayout,
    setRecordingQuality: setRecordingQualityContext,
    showRetryButton,
    showStuckChunksOverlay,
    isMicrophoneMuted,
    setIsMicrophoneMuted,
    setAvatarWebcam,
    setAvatarImage,
    setAvatarImageLoaded,
    edgeToEdge,
    setEdgeToEdge,
    stageRef,
  } = useRecorderContext();
  const { userData } = useUserStore();

  // Phase 1: User permissions and limits
  const { checkFeature, getLimit, isPaid } = useFeatureAccess();
  const {
    isLimited,
    remainingSeconds,
    startCountdown,
    stopCountdown,
    pauseCountdown,
    resumeCountdown,
    formatTime,
    isCountdownActive,
    isLimitExhausted,
    setUsedDuration,
    totalLimitSeconds,
  } = useRecordingLimit();
  const { deviceTier, qualityConfig } = useDeviceCapabilityQuality();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradePromptFeature, setUpgradePromptFeature] = useState("");
  const [upgradePromptDescription, setUpgradePromptDescription] = useState("");

  // Canvas store hooks - read from Context, not Jotai atom (single source of truth)
  const { recordingQuality } = useRecorderContext();
  const [mute, setMute] = useState(!rawMicrophoneStream);
  const [isNoiseCancellation, setIsNoiseCancellation] = useState(false);
  
  // Phase 2: Audio processor selection (studio/rnnoise/none)
  type AudioProcessor = 'studio' | 'rnnoise' | 'none';
  const [audioProcessor, setAudioProcessor] = useState<AudioProcessor>(() => {
    const saved = localStorage.getItem('audio-processor-preference');
    return (saved as AudioProcessor) || 'rnnoise'; // Default to RNNoise for best quality
  });
  const [isPresetLocked, setIsPresetLocked] = useState(false);
  const [selectedMicId, setSelectedMicId] = useState<string | undefined>();
  const [selectedWebcamId, setSelectedWebcamId] = useState<
    string | undefined
  >();
  const [isTeleprompterOpen, setIsTeleprompterOpen] = useAtom(isTeleprompterOpenAtom);
  const [currentScene, setCurrentScene] = useAtom(currentSceneAtom);
  const [sceneList, setSceneList] = useAtom(sceneListAtom);
  const [activeProject] = useAtom(activeProjectAtom);
  const [, updateSceneInList] = useAtom(updateSceneInListAtom);
  
  // Check if in blank/quick recording mode (should show ratio and settings buttons)
  const [searchParams] = useSearchParams();
  const recordingMode = searchParams.get('mode') || 'project';
  const isBlankMode = recordingMode === 'blank';
  
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRetakeModalOpen, setIsRetakeModalOpen] = useState(false);
  const [selectedBackgroundValue, setSelectedBackgroundValue] = useState(
    canvasBackgrounds[0].value
  );

  // Teleprompter state for PiP synchronization
  const [teleprompterText, setTeleprompterText] = useState("");
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [highlightSpeed, setHighlightSpeed] = useState(500);

  // Debounced teleprompter state for PiP updates
  const [debouncedTeleprompterState, setDebouncedTeleprompterState] = useState({
    text: "",
    currentWordIndex: -1,
    isPlaying: false,
  });

  // RTL detection function
  const detectRTL = (text: string): boolean => {
    if (!text.trim()) return false;

    // RTL character ranges
    const rtlRanges = [
      [0x0600, 0x06ff], // Arabic
      [0x0750, 0x077f], // Arabic Supplement
      [0x08a0, 0x08ff], // Arabic Extended-A
      [0xfb50, 0xfdff], // Arabic Presentation Forms-A
      [0xfe70, 0xfeff], // Arabic Presentation Forms-B
      [0x0590, 0x05ff], // Hebrew
      [0xfb1d, 0xfb4f], // Hebrew Presentation Forms
      [0x0780, 0x07bf], // Thaana
      [0x0800, 0x083f], // Samaritan
      [0x0700, 0x074f], // Syriac
      [0x200f, 0x200f], // Right-to-Left Mark
    ];

    // Check for RTL characters in the text
    for (let i = 0; i < text.length; i++) {
      const charCode = text.charCodeAt(i);

      for (const [start, end] of rtlRanges) {
        if (charCode >= start && charCode <= end) {
          return true;
        }
      }
    }

    return false;
  };

  // Debounce teleprompter updates to prevent PiP flickering
  // useEffect(() => {
  //   const timeoutId = setTimeout(() => {
  //     setDebouncedTeleprompterState({
  //       text: teleprompterText,
  //       currentWordIndex,
  //       isPlaying,
  //     });
  //   }, 250); // Debounce for 250ms

  //   return () => clearTimeout(timeoutId);
  // }, [teleprompterText, currentWordIndex, isPlaying]);

  // Custom color picker state
  const [customColorPickerOpen, setCustomColorPickerOpen] = useState(false);
  const [customColor, setCustomColor] = useState("#4a3cfa1f");
  const [customColorHex, setCustomColorHex] = useState("#4a3cfa1f");

  // Custom image upload state
  const [customImageUploadOpen, setCustomImageUploadOpen] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Backgrounds tab state
  const [activeBackgroundTab, setActiveBackgroundTab] = useState("colors");

  const [anyPopoverOpen, setAnyPopoverOpen] = useState(false);

  const [isRecordingButtonHovered, setIsRecordingButtonHovered] =
    useState(false);
  const [timerUpdateTrigger, setTimerUpdateTrigger] = useState(0);
  const currentTimerValueRef = useRef("00:00");
  const totalPauseDurationRef = useRef(0);
  const pauseStartTimeRef = useRef<number | null>(null);
  
  // Loading states for mid-recording stream toggles
  const [isTogglingWebcam, setIsTogglingWebcam] = useState(false);
  const [isTogglingScreen, setIsTogglingScreen] = useState(false);
  // Use refs for immediate blocking (state updates are batched/async)
  const isTogglingWebcamRef = useRef(false);
  const isTogglingScreenRef = useRef(false);

  const isScreenSharing = useMemo(() => !!screenStream, [screenStream]);
  const isWebcamOn = useMemo(() => !!webcamStream, [webcamStream]);
  const isMicStream = useMemo(
    () => !!rawMicrophoneStream,
    [rawMicrophoneStream]
  );

  const isPresentationImport = (currentScene as any)?.metadata?.sourceType === 'presentation-import';

  // Refs to avoid stale closures in callbacks
  const webcamStreamRef = useRef(webcamStream);
  const screenStreamRef = useRef(screenStream);
  
  // Keep refs in sync with state
  useEffect(() => {
    webcamStreamRef.current = webcamStream;
  }, [webcamStream]);
  
  useEffect(() => {
    screenStreamRef.current = screenStream;
  }, [screenStream]);

  const [isModalStreamOpen, setIsModalStreamOpen] = useState(false);
  const [modalNeeds, setModalNeeds] = useState<{
    webcam: boolean;
    screen: boolean;
  }>({ webcam: false, screen: false });

  // stores preset info to apply later
  const [pendingPreset, setPendingPreset] = useState<{
    key: string;
    preset: PresetFn;
    requiresWebcam: boolean;
    requiresScreen: boolean;
  } | null>(null);

  const [isLayoutPopoverOpen, setIsLayoutPopoverOpen] = useState(false);

  // Dynamic toolbar width detection for responsive button visibility
  const { mode: toolbarMode, showPip: showPipButton, toolbarRef } = useResponsiveToolbarModes();
  const isCompactMode = toolbarMode === 'compact' || toolbarMode === 'minimal';
  const isMinimalMode = toolbarMode === 'minimal';

  // Cleanup custom images on unmount
  useEffect(() => {
    return () => {
      // Clean up all custom image blob URLs to prevent memory leaks
      customBackgroundImages.forEach((blobUrl) => {
        URL.revokeObjectURL(blobUrl);
      });
    };
  }, []); // Remove customBackgroundImages dependency to prevent premature cleanup

  // Memoize expensive filter operations
  const solidColors = useMemo(
    () => canvasBackgrounds.filter((bg) => bg.type === "color"),
    []
  );
  const gradients = useMemo(
    () => canvasBackgrounds.filter((bg) => bg.type === "gradient"),
    []
  );
  const images = useMemo(
    () => canvasBackgrounds.filter((bg) => bg.type === "image"),
    []
  );

  const { videoRef: screenVideoRef } = useStreamVideo(screenStream, {
    streamType: "screen",
  });
  const { videoRef: webcamVideoRef } = useStreamVideo(webcamStream);

  // Phase 2: Conditional audio processing based on user selection
  const rnnoiseStream = useNoiseControlledStream(
    rawMicrophoneStream,
    audioProcessor === 'rnnoise' // Only enable RNNoise if selected
  );

  // Determine which stream to use based on processor selection
  const processedMicStream = useMemo(() => {
    if (!rawMicrophoneStream) return null;
    
    switch (audioProcessor) {
      case 'rnnoise':
        return rnnoiseStream;
      case 'studio':
        // Studio processor uses browser's built-in noise suppression
        return rawMicrophoneStream;
      case 'none':
        // No processing - raw stream
        return rawMicrophoneStream;
      default:
        return rawMicrophoneStream;
    }
  }, [audioProcessor, rnnoiseStream, rawMicrophoneStream]);

  // Update microphone stream when processed stream changes
  useEffect(() => {
    if (!processedMicStream) return;
    if (processedMicStream?.id === microphoneStream?.id) return; // 💥 prevent loop

    setMicrophoneStream(processedMicStream);
  }, [
    processedMicStream?.id,
    processedMicStream,
    microphoneStream?.id,
    setMicrophoneStream,
  ]); // only update when actual stream changes
  
  // Persist audio processor preference to localStorage
  useEffect(() => {
    localStorage.setItem('audio-processor-preference', audioProcessor);
  }, [audioProcessor]);

  // Re-acquire mic stream when switching to/from RNNoise while mic is active
  const prevProcessorRef = useRef(audioProcessor);
  useEffect(() => {
    const prev = prevProcessorRef.current;
    prevProcessorRef.current = audioProcessor;

    if (prev === audioProcessor) return;
    if (!rawMicrophoneStream) return;
    if (isMicrophoneMuted) return;

    const switchingToRnnoise = audioProcessor === 'rnnoise' && prev !== 'rnnoise';
    const switchingFromRnnoise = audioProcessor !== 'rnnoise' && prev === 'rnnoise';

    if (!switchingToRnnoise && !switchingFromRnnoise) return;

    const reacquire = async () => {
      rawMicrophoneStream.getTracks().forEach(t => t.stop());
      const streamGetter = audioProcessor === 'rnnoise' ? getRawMicrophoneStream : getMicrophoneStream;
      const newStream = await streamGetter(() => {
        setRawMicrophoneStream(null);
        message.info("Microphone access was stopped.");
      });
      if (newStream) {
        registerStream(newStream);
        setRawMicrophoneStream(newStream);
        const audioTracks = newStream.getAudioTracks();
        if (audioTracks[0]?.getSettings().deviceId) {
          setSelectedMicId(audioTracks[0].getSettings().deviceId);
        }
      }
    };
    reacquire();
  }, [audioProcessor, rawMicrophoneStream, isMicrophoneMuted]);
  
  // Reset audio processor to 'none' for non-paid users if they have a premium option selected
  useEffect(() => {
    if (!isPaid && (audioProcessor === 'rnnoise' || audioProcessor === 'studio')) {
      setAudioProcessor('none');
    }
  }, [isPaid]);

  const { applyPreset } = useLayoutManager();

  // Pass current scene's preset and loaded state to prevent auto-layout from overriding scene settings
  // In blank mode, consider "loaded" immediately since there are no scenes to wait for
  useAutoLayoutPreset({
    screenStream,
    webcamStream,
    roundedWebcam,
    screenVideoRef,
    webcamVideoRef,
    canvasRatio,
    recordingQuality,
    applyPreset,
    setSelectedPreset,
    currentScenePreset: currentScene?.layoutPreset,
    isSceneLoaded: isBlankMode ? true : !!currentScene,
    isRecording,
    useSeparateStreamsMode,
  });

  const isManualPresetRef = useRef<any>(null);

  useEffect(() => {
    isManualPresetRef.current = isManualPreset;
  }, [isManualPreset]);

  useImperativeHandle(ref, () => ({
    checkPresetStreamRequirements: (presetKey: string): boolean => {
      const presetInfo = getPresetByKey(presetKey);
      if (!presetInfo) {
        console.warn(`[RecordingToolbar] Preset not found: ${presetKey}`);
        return true;
      }

      const needsWebcam = presetInfo.requiresWebcam && !webcamStream;
      const needsScreen = presetInfo.requiresScreen && !screenStream;

      if (!needsWebcam && !needsScreen) {
        return true;
      }

      setPendingPreset({
        key: presetKey,
        preset: presetInfo.preset,
        requiresWebcam: presetInfo.requiresWebcam,
        requiresScreen: presetInfo.requiresScreen,
      });
      setModalNeeds({ webcam: needsWebcam, screen: needsScreen });
      setIsModalStreamOpen(true);

      return false;
    },
    stopWebcam: () => {
      if (webcamStreamRef.current) {
        webcamStreamRef.current.getTracks().forEach((track: MediaStreamTrack) => track.stop());
        setWebcamStream(null);
        setRoundedWebcam(false);
        setAvatarWebcam(false);
        setAvatarImage(null);
        setAvatarImageLoaded(false);
      }
    },
    stopScreenShare: () => {
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach((track: MediaStreamTrack) => track.stop());
        setScreenStream(null);
      }
    },
  }), [webcamStream, screenStream, setWebcamStream, setScreenStream, setRoundedWebcam, setAvatarWebcam, setAvatarImage, setAvatarImageLoaded]);

  const handleBackgroundChange = useCallback(
    (value: string) => {
      if (!value) return;
      setSelectedBackgroundValue(value);

      // Check if it's a custom color
      if (value.startsWith("custom-") && !value.startsWith("custom-image-")) {
        const hexColor = value.replace("custom-", "");
        setBackgroundColor(hexColor);
        setBackgroundImage(null);
        setCustomColor(hexColor);
        setCustomColorHex(hexColor);
      }
      // Check if it's a custom image
      else if (value.startsWith("custom-image-")) {
        const imageId = value.replace("custom-image-", "");
        handleCustomImageSelect(imageId);
      }
      // Handle predefined backgrounds
      else {
        const bg = canvasBackgrounds.find((b) => b.value === value);
        if (!bg) return;

        if (bg.type === "color" || bg.type === "gradient") {
          setBackgroundColor(bg.color!);
          setBackgroundImage(null);
        } else if (bg.type === "image" && bg.src) {
          const img = new window.Image();
          img.src = bg.src;
          img.onload = () => {
            setBackgroundImage(img);
          };
          setBackgroundColor(null);
        }
      }
    },
    [setBackgroundColor, setBackgroundImage]
  );

  const { handleStart } = useRecordingStarter();

  // Handle custom color application
  const handleCustomColorApply = useCallback(() => {
    // Validate hex color
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (!hexRegex.test(customColorHex)) {
      message.error("Invalid hex color format");
      return;
    }

    // Apply the custom color
    setBackgroundColor(customColorHex);
    setBackgroundImage(null);

    // Create a temporary background value for the toggle group
    const customBgValue = `custom-${customColorHex}`;
    setSelectedBackgroundValue(customBgValue);
    setCustomColorPickerOpen(false);
    message.success("Custom color applied");
  }, [customColorHex, setBackgroundColor, setBackgroundImage]);

  const backgroundS3KeyRef = useRef<string | null>(null);

  // Handle custom image upload
  const handleImageUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type - only accept JPEG/JPG
      const allowedTypes = ["image/jpeg", "image/jpg"];
      if (!allowedTypes.includes(file.type)) {
        message.error("Please select a valid JPEG/JPG image file");
        return;
      }

      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        message.error("Image size must be less than 5MB");
        return;
      }

      setIsUploadingImage(true);

      // Create blob URL and add to custom images
      const blobUrl = URL.createObjectURL(file);
      const imageId = `custom-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      addCustomBackgroundImage(imageId, blobUrl);

      // Apply the custom image immediately using blob URL for instant preview
      const img = new window.Image();
      img.src = blobUrl;
      img.onload = () => {
        setBackgroundImage(img);
        setBackgroundColor(null);
        setSelectedBackgroundValue(`custom-image-${imageId}`);
        setIsUploadingImage(false);
        setCustomImageUploadOpen(false);
        message.success("Custom image applied");
      };
      img.onerror = () => {
        message.error("Failed to load custom image");
        setIsUploadingImage(false);
        removeCustomBackgroundImage(imageId);
      };

      // Upload the image to S3 in the background for the export pipeline
      (async () => {
        try {
          const res = await fetch("/api/objects/background-upload-url", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          });
          if (!res.ok) throw new Error("Failed to get upload URL");
          const { uploadUrl, key } = await res.json();

          const uploadRes = await fetch(uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": "image/jpeg" },
            body: file,
          });
          if (!uploadRes.ok) throw new Error("Failed to upload to S3");

          backgroundS3KeyRef.current = key;
          setIntendedBackgroundImage(blobUrl, key);
          updateBackgroundKey(key);
          console.log("[BackgroundUpload] Custom image uploaded to S3:", key);
        } catch (err) {
          console.warn("[BackgroundUpload] S3 upload failed (local preview still works):", err);
        }
      })();

      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [
      addCustomBackgroundImage,
      removeCustomBackgroundImage,
      setBackgroundImage,
      setBackgroundColor,
      setIntendedBackgroundImage,
      updateBackgroundKey,
    ]
  );

  // Handle custom image selection
  const handleCustomImageSelect = useCallback(
    (imageId: string) => {
      const blobUrl = customBackgroundImages.get(imageId);

      if (!blobUrl) {
        console.warn("No blob URL found for image ID:", imageId);
        return;
      }

      const img = new window.Image();
      img.src = blobUrl;
      img.onload = () => {
        setBackgroundImage(img);
        setBackgroundColor(null);
        setSelectedBackgroundValue(`custom-image-${imageId}`);
      };
      img.onerror = (error) => {
        console.error("Failed to load custom image:", error);
        console.warn("Blob URL may be invalid:", blobUrl);
        // Remove the invalid blob URL from the map
        removeCustomBackgroundImage(imageId);
        message.error("Custom image is no longer available. Please re-upload.");
      };
    },
    [
      customBackgroundImages,
      setBackgroundImage,
      setBackgroundColor,
      removeCustomBackgroundImage,
    ]
  );

  // Handle custom image removal
  const handleCustomImageRemove = useCallback(
    (imageId: string, event: React.MouseEvent) => {
      try {
        event.stopPropagation();
        removeCustomBackgroundImage(imageId);

        // If this was the selected image, reset to default
        if (selectedBackgroundValue === `custom-image-${imageId}`) {
          setSelectedBackgroundValue(canvasBackgrounds[0].value);
          handleBackgroundChange(canvasBackgrounds[0].value);
        }
      } catch (error) {
        console.log(error);
      }
    },
    [
      removeCustomBackgroundImage,
      selectedBackgroundValue,
      handleBackgroundChange,
    ]
  );

  const { handleOpenPiP, pipWindowRef, isPipOpen } = usePipWindow({
    isRecording,
    isPaused,
    recordingStartTime,
    showCountdown,
    webcamStream,
    isWebcamOn,
    isScreenSharing,
    isUploading,
    showRetryButton,
    showStuckChunksOverlay,
    mirroredWebcam,
    avatarWebcam,
    userData,
    stopRecording,
    pauseRecording,
    resumeRecording,
    deleteRecording,
    handleStart,
    currentTimerValue: currentTimerValueRef.current,
    teleprompterData: {
      text: teleprompterText,
      currentWordIndex,
      isPlaying,
      isRTL: detectRTL(teleprompterText),
      isTeleprompterOpen,
      highlightSpeed,
    },
  });

  useEffect(() => {
    if (
      selectedPreset === "" ||
      selectedPreset === "Preset_ScreenFull" ||
      selectedPreset === "Preset_WebcamFull" ||
      selectedPreset === "Preset_PresentationSlide" ||
      selectedPreset === "Preset_PresentationSlideWithWebcam"
    ) {
      setIsPresetLocked(true);
    } else {
      setIsPresetLocked(false);
    }
  }, [selectedPreset]);

  useEffect(() => {
    if (!isRecording) return;
    if (!screenStream) {
      stopSingleStreamRecording("screen");
    }
    if (!isWebcamOn) {
      stopSingleStreamRecording("webcam");
    }
    if (!microphoneStream) {
      stopSingleStreamRecording("microphone");
    }
  }, [
    isRecording,
    screenStream,
    microphoneStream,
    stopSingleStreamRecording,
    isWebcamOn,
  ]);

  // Calculate used duration from recorded scenes for project mode (cumulative limit)
  useEffect(() => {
    // Only apply cumulative limits for scene-based projects, not blank mode
    if (isBlankMode) {
      setUsedDuration(0);
      return;
    }
    
    // Calculate total duration of all recorded scenes except the current one
    // (current scene will be re-recorded, so don't count it)
    const usedDurationMs = sceneList
      .filter(scene => 
        scene.status === 'recorded' && 
        scene.id !== currentScene?.id // Exclude current scene (will be overwritten on retake)
      )
      .reduce((total, scene) => total + (scene.duration || 0), 0);
    
    const usedDurationSec = Math.floor(usedDurationMs / 1000);
    setUsedDuration(usedDurationSec);
  }, [sceneList, currentScene?.id, isBlankMode, setUsedDuration]);

  // Phase 1: Countdown management - start countdown when recording starts
  useEffect(() => {
    if (isRecording && isLimited && !isPaused) {
      startCountdown();
    }
  }, [isRecording, isLimited, isPaused, startCountdown]);

  // Phase 1: Pause countdown when recording pauses
  useEffect(() => {
    if (isPaused && isCountdownActive) {
      pauseCountdown();
    } else if (isRecording && !isPaused && isLimited && !isCountdownActive) {
      resumeCountdown();
    }
  }, [isPaused, isCountdownActive, isRecording, isLimited, pauseCountdown, resumeCountdown]);

  // Phase 1: Stop countdown when recording stops
  useEffect(() => {
    if (!isRecording && isCountdownActive) {
      stopCountdown();
    }
  }, [isRecording, isCountdownActive, stopCountdown]);

  // Phase 1: Auto-stop recording when countdown reaches zero
  useEffect(() => {
    if (isRecording && isLimited && remainingSeconds === 0) {
      message.warning("Recording limit reached. Stopping recording...");
      stopRecording();
      
      // Phase 3: Show upgrade prompt after stopping
      setTimeout(() => {
        const durationLimit = getLimit("DURATION_PER_VIDEO");
        const limitMinutes = durationLimit !== null ? durationLimit : 5;
        setUpgradePromptFeature("Extended Recording Time");
        setUpgradePromptDescription(
          `You've reached your ${limitMinutes}-minute recording limit. Upgrade to Pro for unlimited recording duration and create longer, more comprehensive videos.`
        );
        setShowUpgradePrompt(true);
      }, 500); // Small delay to ensure recording stops first
    }
  }, [isRecording, isLimited, remainingSeconds, stopRecording, getLimit]);

  // Auto-stop recording when both webcam and screen streams become unavailable mid-recording
  // Skip for presentation-import scenes which can record with mic-only (no video streams required)
  useEffect(() => {
    if (isRecording && !webcamStream && !screenStream && !isPresentationImport) {
      logger.info('[RecordingToolbar] Both webcam and screen streams unavailable, stopping recording');
      message.warning("No active streams. Stopping recording...");
      stopRecording();
    }
  }, [isRecording, webcamStream, screenStream, stopRecording, isPresentationImport]);

  const handleStartScreenShare = useCallback(async () => {
    try {
      const stream = await getScreenStream(() => {
        setScreenStream(null);
        setIsSystemAudioEnabled(false);
        message.info("Screen sharing stopped.");

        // Auto-close PiP window when screen sharing stops via browser
        if (pipWindowRef.current && !pipWindowRef.current.closed) {
          try {
            pipWindowRef.current.close();
            pipWindowRef.current = null;
          } catch (err) {
            logger.error("Failed to close PiP window:", err);
          }
        }
      });

      if (stream) {
        registerStream(stream);
        setScreenStream(stream);
        setHasManualModifications(false);

        const audioTracks = stream.getAudioTracks();
        if (audioTracks.length > 0) {
          setIsSystemAudioEnabled(true);
          message.success("Screen sharing started with audio capture enabled.");
        } else {
          setIsSystemAudioEnabled(false);
        }

        if (!pipWindowRef.current || pipWindowRef.current.closed) {
          await handleOpenPiP();
        }
      }
    } catch (err) {
      logger.error("Failed to start screen share", err);
    }
  }, [setScreenStream, setIsSystemAudioEnabled, setHasManualModifications, handleOpenPiP]);

  const handleStopScreenShare = useCallback(async () => {
    console.log("[ScreenDebug] handleStopScreenShare called", {
      hasScreenStream: !!screenStreamRef.current,
      timestamp: Date.now(),
    });
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => track.stop());
      setScreenStream(null);
      setIsSystemAudioEnabled(false);
      setHasManualModifications(false);

      if (pipWindowRef.current && !pipWindowRef.current.closed) {
        try {
          pipWindowRef.current.close();
          pipWindowRef.current = null;
        } catch (err) {
          logger.error("Failed to close PiP window:", err);
        }
      }
    }
  }, [setScreenStream, setIsSystemAudioEnabled, setHasManualModifications]);

  useEffect(() => {
    if (!screenStream && !webcamStream && !isRecording && !showCountdown) {
      setShowCountdown(false);
      setScreenStream(null);
      setIsSystemAudioEnabled(false);
      setHasManualModifications(false);
    }
  }, [
    screenStream,
    webcamStream,
    isRecording,
    showCountdown,
    setHasManualModifications,
    setIsSystemAudioEnabled,
    setScreenStream,
    setShowCountdown,
  ]);
  const handleStartWebcam = useCallback(async () => {
    try {
      const stream = await getWebcamStream(() => {
        setWebcamStream(null);
        message.info("Webcam access was stopped.");
      });

      if (stream) {
        registerStream(stream);
        setWebcamStream(stream);
        setHasManualModifications(false);
        const videoTracks = stream.getVideoTracks();
        if (videoTracks[0]?.getSettings().deviceId) {
          setSelectedWebcamId(videoTracks[0].getSettings().deviceId);
        }
      }
    } catch (err) {
      logger.error("Webcam access failed", err);
    }
  }, [setWebcamStream, setSelectedWebcamId, setHasManualModifications]);

  // start webcam on page load if permission granted
  // Skip auto-start for presentation-imported scenes (slide-only recording)
  // Wait for scene to load in scene mode before deciding (blank mode has no scenes)
  const isSceneReady = isBlankMode || !!currentScene;
  useEffect(() => {
    if (!isSceneReady) return;
    if (isPresentationImport) return;

    const tryAutoStartWebcam = async () => {
      if (!navigator.permissions || !navigator.mediaDevices) return;

      try {
        const permissionStatus = await navigator.permissions.query({
          name: "camera" as PermissionName, // TS compatibility
        });

        if (permissionStatus.state === "granted") {
          await handleStartWebcam();
        }
      } catch (err) {
        logger.warn("Failed to check camera permission:", err);
      }
    };

    tryAutoStartWebcam();
  }, [handleStartWebcam, isPresentationImport, isSceneReady]);

  const handleStopWebcam = useCallback(async () => {
    if (webcamStreamRef.current) {
      webcamStreamRef.current.getTracks().forEach((track) => track.stop());
      setWebcamStream(null);
      setHasManualModifications(false);
      setRoundedWebcam(false);
      setAvatarWebcam(false);
      setAvatarImage(null);
      setAvatarImageLoaded(false);
    }
  }, [setWebcamStream, setHasManualModifications, setRoundedWebcam, setAvatarWebcam, setAvatarImage, setAvatarImageLoaded]);

  const handleStartMic = useCallback(async () => {
    console.log("[MicDebug] handleStartMic called", {
      hasRawMicrophoneStream: !!rawMicrophoneStream,
      timestamp: Date.now(),
    });
    try {
      if (rawMicrophoneStream) {
        const audioTracks = rawMicrophoneStream.getAudioTracks();
        console.log("[MicDebug] Existing stream found, enabling tracks", {
          trackCount: audioTracks.length,
          tracksEnabled: audioTracks.map(t => t.enabled),
        });
        audioTracks.forEach((track) => {
          track.enabled = true;
        });
        console.log("[MicDebug] Tracks enabled, setting isMicrophoneMuted to false");
        setIsMicrophoneMuted(false);
        return;
      }
      console.log("[MicDebug] No existing stream, requesting new microphone stream", {
        audioProcessor,
        useRawStream: audioProcessor === 'rnnoise',
      });
      const streamGetter = audioProcessor === 'rnnoise' ? getRawMicrophoneStream : getMicrophoneStream;
      const stream = await streamGetter(() => {
        console.log("[MicDebug] Microphone stream ended callback");
        setRawMicrophoneStream(null);
        message.info("Microphone access was stopped.");
      });

      if (stream) {
        console.log("[MicDebug] New stream obtained, registering and setting state", {
          trackCount: stream.getAudioTracks().length,
        });
        registerStream(stream);
        setRawMicrophoneStream(stream);
        setIsMicrophoneMuted(false);

        const audioTracks = stream.getAudioTracks();
        if (audioTracks[0]?.getSettings().deviceId) {
          setSelectedMicId(audioTracks[0].getSettings().deviceId);
        }
      } else {
        console.warn("[MicDebug] getMicrophoneStream returned null/undefined");
      }
    } catch (err) {
      console.error("[MicDebug] Microphone access failed", err);
    }
  }, [rawMicrophoneStream, setRawMicrophoneStream, setIsMicrophoneMuted, audioProcessor]);

  const handleStopMic = useCallback(() => {
    console.log("[MicDebug] handleStopMic called", {
      hasRawMicrophoneStream: !!rawMicrophoneStream,
      timestamp: Date.now(),
    });
    if (rawMicrophoneStream) {
      const audioTracks = rawMicrophoneStream.getAudioTracks();
      console.log("[MicDebug] Disabling tracks", {
        trackCount: audioTracks.length,
        tracksEnabled: audioTracks.map(t => t.enabled),
      });
      audioTracks.forEach((track) => {
        track.enabled = false;
      });
      console.log("[MicDebug] Setting isMicrophoneMuted to true");
      setIsMicrophoneMuted(true);
    } else {
      console.warn("[MicDebug] handleStopMic called but no rawMicrophoneStream exists");
    }
  }, [rawMicrophoneStream, setIsMicrophoneMuted]);

  const handleChangeMicDevice = useCallback(async (deviceId: string) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } },
      });

      if (rawMicrophoneStream) {
        unregisterStream(rawMicrophoneStream);
        rawMicrophoneStream.getTracks().forEach((track) => track.stop());
      }

      registerStream(stream);
      setRawMicrophoneStream(stream);
      setSelectedMicId(deviceId);
    } catch (err) {
      logger.error("Failed to switch microphone device:", err);
    }
  }, [rawMicrophoneStream, setRawMicrophoneStream]);

  const [micForm] = Form.useForm();

  const [webcamForm] = Form.useForm();

  //capture the device ID from the webcam/microphone stream when started automatically
  useEffect(() => {
    if (rawMicrophoneStream) {
      const audioTrack = rawMicrophoneStream.getAudioTracks()[0];
      const deviceId = audioTrack?.getSettings()?.deviceId;
      if (deviceId) {
        setSelectedMicId(deviceId);
      }
    }
  }, [rawMicrophoneStream]);

  useEffect(() => {
    const tryAutoStartMic = async () => {
      if (!navigator.permissions || !navigator.mediaDevices) return;

      try {
        const permissionStatus = await navigator.permissions.query({
          name: "microphone" as PermissionName,
        });

        if (permissionStatus.state === "granted") {
          const streamGetter = audioProcessor === 'rnnoise' ? getRawMicrophoneStream : getMicrophoneStream;
          const stream = await streamGetter(() => {
            setRawMicrophoneStream(null);
            message.info("Microphone access was stopped.");
          });

          if (stream) {
            // Mute all audio tracks
            stream.getAudioTracks().forEach((track) => {
              track.enabled = false;
            });
            registerStream(stream);
            setRawMicrophoneStream(stream);
            setIsMicrophoneMuted(true);

            const audioTracks = stream.getAudioTracks();
            if (audioTracks[0]?.getSettings().deviceId) {
              setSelectedMicId(audioTracks[0].getSettings().deviceId);
            }
          }
        }
      } catch (err) {
        logger.warn("Failed to check microphone permission:", err);
      }
    };

    tryAutoStartMic();
  }, []);

  useEffect(() => {
    if (webcamStream) {
      const videoTrack = webcamStream.getVideoTracks()[0];
      const deviceId = videoTrack?.getSettings()?.deviceId;
      if (deviceId) {
        setSelectedWebcamId(deviceId);
      }
    }
  }, [webcamStream]);

  const handleChangeWebcamDevice = useCallback(async (deviceId: string) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
        audio: false,
      });

      if (webcamStream) {
        unregisterStream(webcamStream);
        webcamStream.getTracks().forEach((track) => track.stop());
      }

      registerStream(stream);
      setWebcamStream(stream);
      setSelectedWebcamId(deviceId);
    } catch (err) {
      logger.error("Failed to switch webcam device:", err);
    }
  }, [webcamStream, setWebcamStream]);

  const [settingsForm] = Form.useForm();

  // Handle Teleprompter cleanup on close
  useEffect(() => {
    const handleUnload = () => {
      localStorage.removeItem("teleprompter_text");
      localStorage.removeItem("teleprompter_speed");
    };

    window.addEventListener("beforeunload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  //Handle Preset Change
  const handlePresetChange = async (key: string | null) => {
    if (!key) return;
    setIsManualPreset(true);
    
    // Emit event to notify CanvasRecorder that user manually changed preset
    // This prevents scene preset re-application on scene re-entry
    window.dispatchEvent(new CustomEvent("manual-preset-change"));
    const presetPool =
      canvasRatio === "16:9"
        ? landscapePresetsWithPreview
        : canvasRatio === "9:16"
        ? portraitPresetsWithPreview
        : squarePresetsWithPreview;

    const selected = presetPool.find((p) => p.key === key);
    if (!selected) return;

    const needsWebcam = selected.requiresWebcam && !isWebcamOn;
    const needsScreen = selected.requiresScreen && !isScreenSharing;
    // All required streams are available
    if (!needsWebcam && !needsScreen) {
      // 💡 Stop unneeded streams before applying preset
      if (!selected.requiresWebcam && isWebcamOn) {
        await handleStopWebcam();
      }
      if (!selected.requiresScreen && isScreenSharing) {
        await handleStopScreenShare();
      }

      // safe ratio calc (screen -> webcam -> canvas ratio fallback)
      const vw =
        screenVideoRef.current?.videoWidth ??
        webcamVideoRef.current?.videoWidth;
      const vh =
        screenVideoRef.current?.videoHeight ??
        webcamVideoRef.current?.videoHeight;

      const ratio =
        vw && vh
          ? vw / vh
          : canvasRatio === "9:16"
          ? 9 / 16
          : canvasRatio === "1:1"
          ? 1
          : 16 / 9;

      // Apply preset first, then update selected preset
      applyPreset(
        selected.preset,
        ratio,
        canvasWidth,
        canvasHeight,
        canvasRatio,
        undefined,
        false,
        undefined,
        true // isUserInitiated - mark onboarding task
      ); // reset manual modifications for user-initiated preset changes

      // Force UI update by setting selected preset after a small delay
      setTimeout(() => {
        setSelectedPreset(key);
      }, 0);
      return;
    }

    // Show modal to request missing streams
    setPendingPreset({
      key,
      preset: selected.preset,
      requiresWebcam: selected.requiresWebcam,
      requiresScreen: selected.requiresScreen,
    });
    setModalNeeds({ webcam: needsWebcam, screen: needsScreen });
    setIsModalStreamOpen(true);
  };

  useEffect(() => {
    if (!isModalStreamOpen || !pendingPreset) return;

    const isScreenRequired = pendingPreset.requiresScreen;
    const isWebcamRequired = pendingPreset.requiresWebcam;

    const webcamReady = !isWebcamRequired || isWebcamOn;
    const screenReady = !isScreenRequired || isScreenSharing;

    const videoEl = screenVideoRef.current;
    if (screenReady && webcamReady) {
      if (isScreenRequired && videoEl) {
        const width = videoEl.videoWidth;
        const height = videoEl.videoHeight;

        // ✅ Already ready? Apply immediately
        if (width && height) {
          const ratio = width / height;
          applyPreset(
            pendingPreset.preset,
            ratio,
            canvasWidth,
            canvasHeight,
            canvasRatio,
            undefined,
            false,
            undefined,
            true // isUserInitiated - mark onboarding task
          );
          setTimeout(() => {
            setSelectedPreset(pendingPreset.key);
          }, 0);
          setPendingPreset(null);
          setIsModalStreamOpen(false);
          return;
        }

        // ⏳ Otherwise wait for metadata
        const handleReady = () => {
          const width = videoEl.videoWidth;
          const height = videoEl.videoHeight;

          if (!width || !height) return;

          const ratio = width / height;
          applyPreset(
            pendingPreset.preset,
            ratio,
            canvasWidth,
            canvasHeight,
            canvasRatio,
            undefined,
            false,
            undefined,
            true // isUserInitiated - mark onboarding task
          );
          setTimeout(() => {
            setSelectedPreset(pendingPreset.key);
          }, 0);
          setPendingPreset(null);
          setIsModalStreamOpen(false);
        };

        videoEl.addEventListener("loadedmetadata", handleReady, {
          once: true,
        });

        return () => {
          videoEl.removeEventListener("loadedmetadata", handleReady);
        };
      }

      // ✅ Webcam-only layout
      const fallbackRatio = 16 / 9;
      applyPreset(
        pendingPreset.preset,
        fallbackRatio,
        canvasWidth,
        canvasHeight,
        canvasRatio,
        undefined,
        false,
        undefined,
        true // isUserInitiated - mark onboarding task
      );
      setTimeout(() => {
        setSelectedPreset(pendingPreset.key);
      }, 0);
      setPendingPreset(null);
      setIsModalStreamOpen(false);
    }
  }, [
    webcamStream,
    screenStream,
    isModalStreamOpen,
    pendingPreset,
    canvasWidth,
    canvasHeight,
    applyPreset,
    screenVideoRef,
    setSelectedPreset,
    isWebcamOn,
    isScreenSharing,
    canvasRatio,
  ]);

  const getModalContent = () => {
    if (modalNeeds.webcam && modalNeeds.screen) {
      return {
        title: "Enable Webcam & Screen Sharing",
        body: "This layout requires both your webcam and screen. Please enable both to continue.",
      };
    } else if (modalNeeds.webcam) {
      return {
        title: "Enable Webcam",
        body: "This layout requires your webcam. Please enable it to continue.",
      };
    } else if (modalNeeds.screen) {
      return {
        title: "Enable Screen Sharing",
        body: "This layout requires screen sharing. Please share your screen to continue.",
      };
    } else {
      return {
        title: "Stream Required",
        body: "This layout requires an active stream to proceed.",
      };
    }
  };

  // Update current timer value when recording time changes
  useEffect(() => {
    if (!isRecording) {
      currentTimerValueRef.current = "00:00";
      totalPauseDurationRef.current = 0;
      pauseStartTimeRef.current = null;
      setTimerUpdateTrigger((prev) => prev + 1); // Force UI update

      // Reset PiP timer when recording stops
      if (pipWindowRef.current && !pipWindowRef.current.closed) {
        try {
          const pipTimerEl =
            pipWindowRef.current.document.getElementById("timer");
          if (pipTimerEl) {
            pipTimerEl.textContent = "00:00";
          }
        } catch (err) {
          // Silently handle cross-origin errors
        }
      }
      return;
    }

    // Handle pause state
    if (isPaused) {
      // Store the time when paused started
      if (pauseStartTimeRef.current === null) {
        pauseStartTimeRef.current = Date.now();
      }
      return;
    }

    // Handle resume state
    if (!isPaused && pauseStartTimeRef.current !== null) {
      // Calculate pause duration and add to total
      const pauseDuration = Date.now() - pauseStartTimeRef.current;
      totalPauseDurationRef.current += pauseDuration;
      pauseStartTimeRef.current = null;
    }

    const interval = setInterval(() => {
      if (recordingStartTime) {
        // Calculate elapsed time minus total pause duration
        const currentTime = Date.now();
        const elapsedTime =
          currentTime - recordingStartTime - totalPauseDurationRef.current;

        const formattedTime = getElapsedTimeFormatted(
          recordingStartTime,
          recordingStartTime + elapsedTime
        );

        // Only update if the value actually changed
        if (currentTimerValueRef.current !== formattedTime) {
          currentTimerValueRef.current = formattedTime;
          // Use minimal state update to trigger re-render only when value changes
          setTimerUpdateTrigger((prev) => prev + 1);

          // Update PiP timer directly if PiP window is open
          if (pipWindowRef.current && !pipWindowRef.current.closed) {
            try {
              const pipTimerEl =
                pipWindowRef.current.document.getElementById("timer");
              if (pipTimerEl) {
                pipTimerEl.textContent = formattedTime;
              }
            } catch (err) {
              // Silently handle cross-origin errors
            }
          }
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isRecording, isPaused, recordingStartTime, pipWindowRef]);

  useEffect(() => {
    if (isRecording && !showCountdown) {
      setIsRecordingButtonHovered(false);
    }
  }, [isRecording, showCountdown]);

  // Close custom color picker when popover closes
  useEffect(() => {
    if (!anyPopoverOpen) {
      setCustomColorPickerOpen(false);
    }
  }, [anyPopoverOpen]);

  const ToolbarButtonStyle: React.CSSProperties = {
    height: "auto",
    paddingBlock: 8,
    borderRadius: 6,
  };

  const CompactTooltip = forwardRef<HTMLSpanElement, { title: string; children: React.ReactNode }>(
    ({ title, children }, ref) => {
      if (!isCompactMode) {
        return <span ref={ref} style={{ display: 'contents' }}>{children}</span>;
      }
      return (
        <Tooltip title={title} placement="top">
          <span ref={ref} style={{ display: 'inline-block' }}>{children}</span>
        </Tooltip>
      );
    }
  );

  return (
    <>
      {/* Mute Popup Overlay */}

      <div className="recordingToolbar-wrapper" ref={toolbarRef}>
        <Flex
          gap={isCompactMode ? 6 : 16}
          align="center"
          className={`recordingToolbar ${isCompactMode ? 'toolbar-compact' : ''} ${isMinimalMode ? 'toolbar-minimal' : ''}`}
          style={{ position: "relative" }}
        >
        {/* Section 1 */}
        <Flex gap={4} align="center">
          {/* Webcam */}
          <Space.Compact style={{ alignItems: "center" }}>
            <CompactTooltip title={isWebcamOn ? "Hide Camera" : "Show Camera"}>
              <Button
                type="text"
                size="large"
                disabled={(isRecording && !useSeparateStreamsMode) || isTogglingWebcam}
                loading={isTogglingWebcam}
                danger={!isWebcamOn}
                onClick={() => {
                  if (isRecording && useSeparateStreamsMode) {
                    // Prevent multiple clicks during async operation - use ref for immediate blocking
                    if (isTogglingWebcamRef.current) {
                      return;
                    }
                    isTogglingWebcamRef.current = true;
                    setIsTogglingWebcam(true);
                    
                    // Use requestAnimationFrame to ensure UI updates before heavy work
                    requestAnimationFrame(async () => {
                      try {
                        // During separate streams recording, toggle creates/ends segments
                        const webcamLayout = initialStreamLayout.find(l => l.stream === 'webcam');
                        if (isWebcamOn) {
                          // Hide webcam - end current segment first, then stop the stream
                          await toggleSeparateStream('webcam', false);
                          handleStopWebcam();
                        } else {
                          // Show webcam - get stream first, then start new segment with the stream
                          const stream = await getWebcamStream(() => {
                            setWebcamStream(null);
                            message.info("Webcam access was stopped.");
                          });
                          
                          if (stream) {
                            registerStream(stream);
                            setWebcamStream(stream);
                            setHasManualModifications(false);
                            
                            const videoTracks = stream.getVideoTracks();
                            if (videoTracks[0]?.getSettings().deviceId) {
                              setSelectedWebcamId(videoTracks[0].getSettings().deviceId);
                            }
                            
                            // Calculate the correct position from the preset that will be applied
                            // when both streams are available (screen is already active)
                            let position = webcamLayout;
                            
                            if (screenStream && screenVideoRef?.current) {
                              // Both streams will be active, calculate position from default preset
                              const screenEl = screenVideoRef.current;
                              const screenAspectRatio = screenEl.videoWidth && screenEl.videoHeight
                                ? screenEl.videoWidth / screenEl.videoHeight
                                : 16 / 9;
                              
                              // Get the preset based on canvas ratio
                              let presetFn: PresetFn;
                              if (canvasRatio === "16:9") {
                                presetFn = landscapePresets.Preset_Default;
                              } else if (canvasRatio === "1:1") {
                                presetFn = squarePresets.Preset_CamBottomRight;
                              } else if (canvasRatio === "9:16") {
                                presetFn = portraitPresets.Preset_SideCamStacked;
                              } else {
                                presetFn = landscapePresets.Preset_Default;
                              }
                              
                              // Calculate the layout from the preset
                              const layout = presetFn(
                                canvasWidth,
                                canvasHeight,
                                screenAspectRatio,
                                roundedWebcam,
                                canvasRatio,
                                edgeToEdge
                              );
                              
                              // Find webcam position in the calculated layout
                              const webcamFromPreset = layout.find(l => l.stream === 'webcam');
                              if (webcamFromPreset) {
                                position = webcamFromPreset;
                                logger.info('[WebcamToggle] Calculated position from preset:', position);
                              }
                            }
                            
                            // Fallback to initialStreamLayout or default if no position calculated
                            if (!position) {
                              position = { stream: 'webcam' as const, x: 16, y: 16, width: 308, height: 344 };
                            }
                            
                            await toggleSeparateStream('webcam', true, position, stream);
                          }
                        }
                      } catch (err) {
                        logger.error("Failed to toggle webcam during recording", err);
                      } finally {
                        isTogglingWebcamRef.current = false;
                        setIsTogglingWebcam(false);
                      }
                    });
                  } else {
                    // Not recording - use regular toggle
                    // Guard against double clicks
                    if (isTogglingWebcamRef.current) {
                      return;
                    }
                    if (isWebcamOn) {
                      handleStopWebcam();
                    } else {
                      handleStartWebcam();
                    }
                  }
                }}
                style={{
                  ...ToolbarButtonStyle,
                  width: isCompactMode ? 'auto' : 90,
                }}
              >
                <Flex vertical gap={2} align="center">
                  {isWebcamOn ? <Video size={24} /> : <VideoOff size={24} />}
                  {!isCompactMode && (
                    <>
                      {isWebcamOn ? "Hide " : "Show "}
                      Cam
                    </>
                  )}
                </Flex>
              </Button>
            </CompactTooltip>

            <Popover
              trigger="click"
              placement="top"
              onOpenChange={(open) => setAnyPopoverOpen(open)}
              content={
                <div style={{ width: 260 }}>
                  <Form
                    form={webcamForm}
                    layout="vertical"
                    initialValues={{
                      webcam: selectedWebcamId,
                    }}
                    style={{ width: "100%" }}
                  >
                    {/* <Form.Item
                      label="Rounded Camera?"
                      name="countdown"
                      layout="horizontal"
                      labelAlign="left"
                      colon={false}
                      labelCol={{ span: 16 }}
                    >
                      <Switch
                        style={{
                          marginInlineStart: "auto",
                          display: "flex",
                        }}
                        checked={roundedWebcam}
                        onChange={(checked) => setRoundedWebcam(checked)}
                      />
                    </Form.Item> */}
                    <Form.Item label="Devices" name="webcam">
                      <Select
                        onChange={(value) => {
                          webcamForm.setFieldsValue({
                            webcam: value,
                          });
                          handleChangeWebcamDevice(value);
                        }}
                        options={webcamDevices.map((device) => ({
                          label: device.label || "Webcam",
                          value: device.deviceId,
                        }))}
                      />
                    </Form.Item>
                  </Form>
                </div>
              }
            >
              <Button
                type="link"
                size="large"
                style={{ width: 24 }}
                icon={<ChevronDown size={18} />}
                disabled={!isWebcamOn || isRecording}
              />
            </Popover>
          </Space.Compact>

          {/* Microphone */}
          <Space.Compact style={{ alignItems: "center" }}>
            <CompactTooltip title={!isMicrophoneMuted ? "Disable Mic" : "Enable Mic"}>
              <Button
                type="text"
                danger={isMicrophoneMuted}
                size="large"
                onClick={() => {
                  console.log("[MicDebug] Button onClick fired", {
                    isMicrophoneMuted,
                    willCall: isMicrophoneMuted ? "handleStartMic" : "handleStopMic",
                    timestamp: Date.now(),
                  });
                  if (isMicrophoneMuted) {
                    handleStartMic();
                  } else {
                    handleStopMic();
                  }
                }}
                style={{
                  ...ToolbarButtonStyle,
                  width: isCompactMode ? 'auto' : 90,
                }}
              >
                <Flex vertical gap={2} align="center">
                  {!isMicrophoneMuted ? <Mic size={24} /> : <MicOff size={24} />}
                  {!isCompactMode && (
                    <>
                      {!isMicrophoneMuted ? "Disable " : "Enable "}
                      Mic
                    </>
                  )}
                </Flex>
              </Button>
            </CompactTooltip>

            <Popover
              trigger="click"
              placement="top"
              onOpenChange={(open) => setAnyPopoverOpen(open)}
              content={
                <div style={{ width: 300 }}>
                  <Form
                    form={micForm}
                    layout="vertical"
                    initialValues={{
                      microphone: selectedMicId,
                      audioProcessor: audioProcessor,
                    }}
                    style={{ width: "100%" }}
                  >
                    <div style={{ marginBottom: 24 }}>
                      <Typography.Text style={{ display: 'block', marginBottom: 8 }}>
                        Audio Processor
                      </Typography.Text>
                      <Select
                        value={audioProcessor}
                        onChange={(value) => {
                          // Gate premium audio processors for non-paid users
                          if ((value === 'rnnoise' || value === 'studio') && !isPaid) {
                            setUpgradePromptFeature("Advanced Audio Processing");
                            setUpgradePromptDescription(
                              value === 'rnnoise' 
                                ? "RNNoise uses professional ML-based noise suppression for crystal clear audio. Upgrade to Pro to unlock this feature."
                                : "Studio processing provides balanced audio enhancement. Upgrade to Pro to unlock this feature."
                            );
                            setShowUpgradePrompt(true);
                            return;
                          }
                          setAudioProcessor(value as AudioProcessor);
                        }}
                        disabled={isRecording}
                        style={{ width: '100%' }}
                        options={[
                          {
                            label: (
                              <Flex align="center" gap={8}>
                                <span>🎙️ RNNoise (Max Suppression)</span>
                                {!isPaid && <Crown size={14} fill="#ffb229" color="#ffb229" />}
                              </Flex>
                            ),
                            value: 'rnnoise',
                          },
                          {
                            label: (
                              <Flex align="center" gap={8}>
                                <span>🎚️ Studio (Balanced)</span>
                                {!isPaid && <Crown size={14} fill="#ffb229" color="#ffb229" />}
                              </Flex>
                            ),
                            value: 'studio',
                          },
                          {
                            label: '🔇 None',
                            value: 'none',
                          },
                        ]}
                      />
                      <Typography.Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                        {audioProcessor === 'rnnoise' 
                          ? 'Professional ML-based noise suppression (recommended)'
                          : audioProcessor === 'studio'
                          ? 'Browser built-in balanced processing'
                          : 'No audio processing applied'}
                      </Typography.Text>
                    </div>
                    <Form.Item
                      layout="vertical"
                      label="Microphone Device"
                      name="microphone"
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      style={{ flex: 1 }}
                    >
                      <Select
                        value={selectedMicId}
                        onChange={handleChangeMicDevice}
                        options={micDevices.map((device) => ({
                          label: device.label || "Microphone",
                          value: device.deviceId,
                        }))}
                      />
                    </Form.Item>
                    <VolumeProgress />
                  </Form>
                </div>
              }
            >
              <Button
                type="link"
                size="large"
                style={{ width: 24 }}
                icon={<ChevronDown size={18} />}
                disabled={!isMicStream || isMicrophoneMuted || isRecording}
              />
            </Popover>
          </Space.Compact>

          {/* Screen Sharing */}
          <CompactTooltip title={isScreenSharing ? "Hide Screen" : "Show Screen"}>
            <Button
              type="text"
              size="large"
              disabled={(isRecording && !useSeparateStreamsMode) || isTogglingScreen}
              loading={isTogglingScreen}
              danger={!isScreenSharing}
              onClick={() => {
                if (isRecording && useSeparateStreamsMode) {
                  // Prevent multiple clicks during async operation - use ref for immediate blocking
                  if (isTogglingScreenRef.current) return;
                  isTogglingScreenRef.current = true;
                  setIsTogglingScreen(true);
                  
                  // Use requestAnimationFrame to ensure UI updates before heavy work
                  requestAnimationFrame(async () => {
                    try {
                      // During separate streams recording, toggle creates/ends segments
                      const screenLayout = initialStreamLayout.find(l => l.stream === 'screen');
                      if (isScreenSharing) {
                        // Hide screen - end current segment first, then stop the stream
                        await toggleSeparateStream('screen', false);
                        handleStopScreenShare();
                      } else {
                        // Show screen - get stream first, then start new segment with the stream
                        const stream = await getScreenStream(() => {
                          setScreenStream(null);
                          setIsSystemAudioEnabled(false);
                          message.info("Screen sharing stopped.");
                        });
                        
                        if (stream) {
                          registerStream(stream);
                          setScreenStream(stream);
                          setHasManualModifications(false);
                          
                          const audioTracks = stream.getAudioTracks();
                          if (audioTracks.length > 0) {
                            setIsSystemAudioEnabled(true);
                          }
                          
                          // Calculate the correct position from the preset that will be applied
                          // when both streams are available (webcam may already be active)
                          let position = screenLayout;
                          
                          // Get screen aspect ratio from the new stream
                          const videoTracks = stream.getVideoTracks();
                          const settings = videoTracks[0]?.getSettings();
                          const screenAspectRatio = settings?.width && settings?.height
                            ? settings.width / settings.height
                            : 16 / 9;
                          
                          if (webcamStream) {
                            // Both streams will be active, calculate position from default preset
                            let presetFn: PresetFn;
                            if (canvasRatio === "16:9") {
                              presetFn = landscapePresets.Preset_Default;
                            } else if (canvasRatio === "1:1") {
                              presetFn = squarePresets.Preset_CamBottomRight;
                            } else if (canvasRatio === "9:16") {
                              presetFn = portraitPresets.Preset_SideCamStacked;
                            } else {
                              presetFn = landscapePresets.Preset_Default;
                            }
                            
                            // Calculate the layout from the preset
                            const layout = presetFn(
                              canvasWidth,
                              canvasHeight,
                              screenAspectRatio,
                              roundedWebcam,
                              canvasRatio,
                              edgeToEdge
                            );
                            
                            // Find screen position in the calculated layout
                            const screenFromPreset = layout.find(l => l.stream === 'screen');
                            if (screenFromPreset) {
                              position = screenFromPreset;
                              logger.info('[ScreenToggle] Calculated position from preset:', position);
                            }
                          } else {
                            // Only screen will be active, use full screen preset
                            const layout = Preset_ScreenFull(
                              canvasWidth,
                              canvasHeight,
                              screenAspectRatio,
                              roundedWebcam,
                              canvasRatio,
                              edgeToEdge
                            );
                            const screenFromPreset = layout.find(l => l.stream === 'screen');
                            if (screenFromPreset) {
                              position = screenFromPreset;
                              logger.info('[ScreenToggle] Calculated position from ScreenFull preset:', position);
                            }
                          }
                          
                          // Fallback if no position calculated
                          if (!position) {
                            position = { stream: 'screen' as const, x: 16, y: 138, width: 924, height: 443 };
                          }
                          
                          await toggleSeparateStream('screen', true, position, stream);
                        }
                      }
                    } catch (err) {
                      logger.error("Failed to toggle screen share during recording", err);
                    } finally {
                      isTogglingScreenRef.current = false;
                      setIsTogglingScreen(false);
                    }
                  });
                } else {
                  // Not recording - use regular toggle
                  if (isScreenSharing) {
                    handleStopScreenShare();
                  } else {
                    handleStartScreenShare();
                  }
                }
              }}
              style={{
                ...ToolbarButtonStyle,
                width: isCompactMode ? 'auto' : 110,
              }}
            >
              <Flex vertical gap={2} align="center">
                {isScreenSharing ? (
                  <div style={{ position: "relative", display: "inline-block" }}>
                    <ScreenShare size={24} />

                    {screenStream && (
                      <div
                        style={{
                          position: "absolute",
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "#52c41a",
                          border: "1px solid white",
                          boxShadow: "0 0 0 1px rgba(0,0,0,0.1)",
                          top: -7,
                          right: -8,
                          animation: "pulse 2s infinite",
                        }}
                      />
                    )}
                  </div>
                ) : (
                  <ScreenShareOff size={24} />
                )}
                {!isCompactMode && (
                  <>
                    {isScreenSharing ? "Hide " : "Show "}
                    Screen
                  </>
                )}
              </Flex>
            </Button>
          </CompactTooltip>
        </Flex>
        <Divider type="vertical" style={{ height: 32 }} orientationMargin={0} />

        {/* Section 2 */}
        <Flex gap={12} align="center">
          {/* Ratio button - show for quick recording mode (blank mode) or when no active project */}
          {!isRecording && !showCountdown && (!activeProject || isBlankMode) && (
            <Popover
              trigger="click"
              placement="top"
              onOpenChange={(open) => setAnyPopoverOpen(open)}
              content={
                <div style={{ width: 260 }}>
                  <Radio.Group
                    defaultValue="16:9"
                    block
                    className="recordingRatio"
                    onChange={(e) => {
                      const value = e.target.value;
                      
                      // CRITICAL: Set transitioning flag BEFORE ratio change
                      // This immediately hides existing objects to prevent distortion blink
                      store.set(isRatioTransitioningAtom, true);
                      
                      setCanvasRatio(value);
                      
                      // Emit event to notify CanvasRecorder that user manually changed ratio
                      // This prevents scene preset re-application on scene re-entry
                      window.dispatchEvent(new CustomEvent("manual-preset-change"));

                      // Don't override fullScreen/fullCam
                      if (!isPresetLocked) {
                        let presetPool: any[] = [];

                        if (value === "16:9") {
                          presetPool = landscapePresetsWithPreview;
                        } else if (value === "9:16") {
                          presetPool = portraitPresetsWithPreview;
                        } else if (value === "1:1") {
                          presetPool = squarePresetsWithPreview;
                        }

                        const firstPreset = presetPool[0];

                        if (firstPreset) {
                          setSelectedPreset(firstPreset.key);

                          // Clear current layout and apply the new preset
                          setInitialStreamLayout([]);
                          setTimeout(() => {
                            const videoEl =
                              screenVideoRef.current || webcamVideoRef.current;
                            if (videoEl && videoEl.videoWidth > 0) {
                              const ratio =
                                videoEl.videoWidth / videoEl.videoHeight;

                              // applyPreset(
                              //   firstPreset.preset,
                              //   ratio,
                              //   canvasWidth,
                              //   canvasHeight,
                              //   canvasRatio,
                              //   undefined,
                              //   false
                              // );
                            }
                          }, 0);
                        }
                      }
                    }}
                  >
                    <Radio.Button value="16:9">
                      <svg
                        width="48"
                        height="27"
                        viewBox="0 0 48 27"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="1"
                          y="1"
                          width="46"
                          height="25"
                          rx="4"
                          stroke="#4A3CFA"
                          strokeWidth="2"
                          fill="none"
                        />
                      </svg>

                      <div>Landscape (16:9)</div>
                      <div className="hint" style={{ fontSize: 12 }}>
                        YouTube, presentations, desktop viewing...
                      </div>
                    </Radio.Button>
                    <Radio.Button value="1:1">
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 32 32"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="1"
                          y="1"
                          width="30"
                          height="30"
                          rx="4"
                          stroke="#4A3CFA"
                          strokeWidth="2"
                          fill="none"
                        />
                      </svg>

                      <div>Square (1:1)</div>
                      <div className="hint" style={{ fontSize: 12 }}>
                        Instagram feed, social ads...
                      </div>
                    </Radio.Button>
                    <Radio.Button value="9:16">
                      <svg
                        width="27"
                        height="48"
                        viewBox="0 0 27 48"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="1"
                          y="1"
                          width="25"
                          height="46"
                          rx="4"
                          stroke="#4A3CFA"
                          strokeWidth="2"
                          fill="none"
                        />
                      </svg>

                      <div>Portrait (9:16)</div>
                      <div className="hint" style={{ fontSize: 12 }}>
                        TikTok, Instagram Reels, YouTube Shorts
                      </div>
                    </Radio.Button>
                  </Radio.Group>
                </div>
              }
            >
              <Button
                type="text"
                size="large"
                onClick={() => {}}
                style={{
                  ...ToolbarButtonStyle,
                  width: 70,
                }}
              >
                <Flex vertical gap={2} align="center">
                  <Ratio size={24} />
                  <>Ratio</>
                </Flex>
              </Button>
            </Popover>
          )}
          {/* Ratio */}

          {/* Background - disabled for presentation-imported scenes (slide backgrounds are locked) */}
          <Popover
            trigger="click"
            placement="top"
            open={(currentScene as any)?.metadata?.sourceType === 'presentation-import' ? false : undefined}
            onOpenChange={(open) => setAnyPopoverOpen(open)}
            title={
              <Title
                level={4}
                style={{ marginBlockStart: 8, paddingInline: 16 }}
              >
                Backgrounds
              </Title>
            }
            content={
              <div
                style={{
                  width: 360,
                  padding: "0px 16px 12px",
                }}
              >
                {/* Segmented Navigation */}
                <Segmented
                  options={[
                    { label: "Colors", value: "colors" },
                    { label: "Gradients", value: "gradients" },
                    { label: "Images", value: "images" },
                  ]}
                  block
                  size="large"
                  value={activeBackgroundTab}
                  onChange={(value) => setActiveBackgroundTab(value as string)}
                  style={{ marginBottom: 16, width: "100%" }}
                />

                <div style={{ minHeight: 290 }}>
                  {/* Content based on selected tab */}
                  {activeBackgroundTab === "colors" && (
                    <div>
                      <ToggleGroup.Root
                        type="single"
                        className="recordingColorsToggle"
                        aria-label="Canvas Background"
                        value={selectedBackgroundValue}
                        onValueChange={handleBackgroundChange}
                      >
                        {solidColors.map((bg) => (
                          <ToggleGroup.Item
                            key={bg.value}
                            className="recordingColorsItem"
                            value={bg.value}
                            style={{
                              background: bg.color,
                              boxShadow:
                                bg.value === "white"
                                  ? "inset 0 0 0 1px var(--dadan-border-color)"
                                  : undefined,
                            }}
                            aria-label={bg.label}
                          />
                        ))}
                        {/* Custom Color Picker Toggle */}
                        <ToggleGroup.Item
                          key="custom-color"
                          className="recordingColorsItem recordingCustomColorsItem"
                          value={
                            selectedBackgroundValue.startsWith("custom-")
                              ? selectedBackgroundValue
                              : ""
                          }
                          style={{
                            background: customColor,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          aria-label="Custom Color"
                          onClick={() =>
                            setCustomColorPickerOpen(!customColorPickerOpen)
                          }
                        >
                          <Pipette size={16} color="#4a3cfa" />
                        </ToggleGroup.Item>
                      </ToggleGroup.Root>

                      {/* Custom Color Picker Section */}
                      {customColorPickerOpen && (
                        <div
                          style={{
                            padding: "12px",
                            backgroundColor: "#f8f9fa",
                            borderRadius: "6px",
                            border: "1px solid #e8e8e8",
                            marginTop: 12,
                          }}
                        >
                          <div style={{ marginBottom: "12px" }}>
                            <HexColorPicker
                              color={customColor}
                              onChange={(color) => {
                                setCustomColor(color);
                                setCustomColorHex(color);
                              }}
                              style={{ width: "100%" }}
                            />
                          </div>
                          <Flex gap={8} align="center">
                            <Input
                              value={customColorHex}
                              onChange={(e) => {
                                setCustomColorHex(e.target.value);
                                // Update color picker if valid hex
                                const hexRegex =
                                  /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
                                if (hexRegex.test(e.target.value)) {
                                  setCustomColor(e.target.value);
                                }
                              }}
                              placeholder="#000000"
                              style={{ flex: 1 }}
                            />
                            <Button
                              type="primary"
                              onClick={handleCustomColorApply}
                            >
                              Apply
                            </Button>
                          </Flex>
                        </div>
                      )}
                    </div>
                  )}

                  {activeBackgroundTab === "gradients" && (
                    <div>
                      <ToggleGroup.Root
                        type="single"
                        className="recordingColorsToggle"
                        aria-label="Canvas Gradient"
                        value={selectedBackgroundValue}
                        onValueChange={handleBackgroundChange}
                      >
                        {gradients.map((bg) => (
                          <ToggleGroup.Item
                            key={bg.value}
                            className="recordingColorsItem"
                            value={bg.value}
                            style={{
                              background: bg.color,
                            }}
                            aria-label={bg.label}
                          />
                        ))}
                      </ToggleGroup.Root>
                    </div>
                  )}

                  {activeBackgroundTab === "images" && (
                    <div>
                      {/* Predefined Images */}
                      <ToggleGroup.Root
                        type="single"
                        className="recordingColorsToggle"
                        aria-label="Canvas Image"
                        value={selectedBackgroundValue}
                        onValueChange={handleBackgroundChange}
                      >
                        {images.map((bg) => (
                          <ToggleGroup.Item
                            key={bg.value}
                            className="recordingColorsItem"
                            value={bg.value}
                            style={{
                              background: `url(${bg.thumbnail || bg.src}) center/cover`,
                            }}
                            aria-label={bg.label}
                          />
                        ))}
                      </ToggleGroup.Root>

                      <Divider
                        style={{
                          borderBlockStart: "1px solid rgba(0,0,0,0.08)",
                        }}
                      />

                      {/* Custom Images Section */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginTop: 16,
                          marginBottom: 12,
                        }}
                      >
                        <Title level={5} style={{ marginBlock: 0 }}>
                          Make it yours ✨
                        </Title>
                        <Button
                          variant="filled"
                          color="primary"
                          icon={<Upload size={14} />}
                          onClick={() => {
                            if (!isPaid) {
                              setUpgradePromptFeature("Custom Backgrounds");
                              setUpgradePromptDescription("Upgrade to Pro to upload your own custom background images for a personalized recording experience.");
                              setShowUpgradePrompt(true);
                              return;
                            }
                            fileInputRef.current?.click();
                          }}
                          loading={isUploadingImage}
                          disabled={customBackgroundImages.size >= 3}
                          style={{ padding: "4px 8px" }}
                        >
                          Upload an image
                        </Button>
                      </div>

                      {/* Hidden file input */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".jpg,.jpeg,image/jpeg"
                        onChange={handleImageUpload}
                        style={{ display: "none" }}
                      />

                      {/* Custom Images */}
                      {(customBackgroundImages.size > 0 ||
                        isUploadingImage) && (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(4, 1fr)",
                            gap: 8,
                            marginBottom: 8,
                          }}
                        >
                          {isUploadingImage && (
                            <div
                              style={{
                                aspectRatio: "1",
                                borderRadius: 6,
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#f5f5f5",
                                border: "2px dashed #d9d9d9",
                              }}
                            >
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                Uploading...
                              </Text>
                            </div>
                          )}
                          {Array.from(customBackgroundImages.entries()).map(
                            ([imageId, blobUrl]) => (
                              <div
                                key={imageId}
                                style={{
                                  position: "relative",
                                  aspectRatio: "1",
                                  borderRadius: 6,
                                  overflow: "hidden",
                                  cursor: "pointer",
                                  boxShadow:
                                    selectedBackgroundValue ===
                                    `custom-image-${imageId}`
                                      ? "0 0 0 3px rgba(var(--dadan-primary-rgb), 0.5) !important"
                                      : "none",
                                }}
                                onClick={() => handleCustomImageSelect(imageId)}
                              >
                                <img
                                  src={blobUrl}
                                  alt="Custom background"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<X size={8} />}
                                  onClick={(e) =>
                                    handleCustomImageRemove(imageId, e)
                                  }
                                  style={{
                                    position: "absolute",
                                    top: 2,
                                    right: 2,
                                    width: 12,
                                    height: 12,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    paddingBottom: 4, // Force override
                                    minWidth: 12,
                                    minHeight: 12,
                                    backgroundColor: "rgba(0,0,0,0.6)",
                                    color: "white",
                                    border: "none",
                                  }}
                                />
                              </div>
                            )
                          )}
                        </div>
                      )}

                      {/* Upload Instructions */}
                      {customBackgroundImages.size === 0 &&
                        !isUploadingImage && (
                          <Text
                            type="secondary"
                            style={{
                              fontSize: 14,
                              textAlign: "center",
                              display: "block",
                              marginBlockStart: 16,
                              marginBottom: 0,
                            }}
                          >
                            No Custom Images Yet
                          </Text>
                        )}
                      <Text
                        type="secondary"
                        style={{
                          fontSize: 12,
                          textAlign: "center",
                          fontStyle: "italic",
                          display: "block",
                        }}
                      >
                        JPG/JPEG • Max 5 MB • Best at 1920×1080
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            }
          >
            <Tooltip title={(currentScene as any)?.metadata?.sourceType === 'presentation-import' ? "Background is locked for imported slides" : (isCompactMode ? "Background" : undefined)} placement="top">
              <Button
                type="text"
                size="large"
                disabled={(currentScene as any)?.metadata?.sourceType === 'presentation-import'}
                style={{
                  ...ToolbarButtonStyle,
                  width: isCompactMode ? 'auto' : 100,
                  ...((currentScene as any)?.metadata?.sourceType === 'presentation-import' ? { opacity: 0.45, cursor: 'not-allowed' } : {}),
                }}
              >
                <Flex vertical gap={2} align="center">
                  <Image size={24} />
                  {!isCompactMode && <>Background</>}
                </Flex>
              </Button>
            </Tooltip>
          </Popover>

          {/* Layout */}
          <Popover
            trigger="click"
            placement="top"
            open={isLayoutPopoverOpen}
            onOpenChange={(open) => {
              setIsLayoutPopoverOpen(open);
              setAnyPopoverOpen(open);
            }}
            title={
              <Title
                level={4}
                style={{ marginBlockStart: 8, paddingInline: 16 }}
              >
                Layouts
              </Title>
            }
            content={
              <div
                style={{
                  width: 380,
                  padding: "8px 16px 12px",
                }}
              >
                {/* Edge-to-Edge toggle */}
                <Form
                  layout="vertical"
                  style={{ width: "100%", marginBottom: 16 }}
                >
                  <Form.Item
                    label="Edge-to-Edge (no padding)"
                    name="edge2edge"
                    layout="horizontal"
                    labelAlign="left"
                    colon={false}
                    labelCol={{ span: 16 }}
                    style={{ marginBottom: 0 }}
                  >
                    <Switch
                      style={{
                        marginInlineStart: "auto",
                        display: "flex",
                      }}
                      checked={edgeToEdge}
                      onChange={(checked) => {
                        setEdgeToEdge(checked);
                        // Reapply current preset with new edge-to-edge setting
                        if (selectedPreset) {
                          // Get all available presets for current ratio
                          const presetPool =
                            canvasRatio === "16:9"
                              ? landscapePresetsWithPreview
                              : canvasRatio === "9:16"
                              ? portraitPresetsWithPreview
                              : squarePresetsWithPreview;

                          // Try to find the current preset in the pool
                          let currentPreset = presetPool.find(
                            (p) => p.key === selectedPreset
                          );

                          // If not found in pool, it might be an auto-applied preset
                          if (!currentPreset) {
                            // Check if it's one of the auto-applied presets
                            if (selectedPreset === "Preset_ScreenFull") {
                              currentPreset = {
                                key: "Preset_ScreenFull",
                                preset: Preset_ScreenFull,
                                name: "Screen Full",
                                preview: "",
                                requiresWebcam: false,
                                requiresScreen: true,
                              };
                            } else if (selectedPreset === "Preset_WebcamFull") {
                              currentPreset = {
                                key: "Preset_WebcamFull",
                                preset: Preset_WebcamFull,
                                name: "Webcam Full",
                                preview: "",
                                requiresWebcam: true,
                                requiresScreen: false,
                              };
                            }
                          }

                          if (currentPreset) {
                            const videoEl =
                              screenVideoRef.current || webcamVideoRef.current;
                            if (videoEl && videoEl.videoWidth > 0) {
                              const ratio =
                                videoEl.videoWidth / videoEl.videoHeight;
                              applyPreset(
                                currentPreset.preset,
                                ratio,
                                canvasWidth,
                                canvasHeight,
                                canvasRatio,
                                undefined,
                                hasManualModifications,
                                checked // Pass the new edgeToEdge value directly
                              );
                            }
                          }
                        }
                      }}
                    />
                  </Form.Item>
                </Form>

                <div>
                  <ToggleGroup.Root
                    type="single"
                    className="recordingLayoutsToggle"
                    aria-label="Layout Presets"
                    value={selectedPreset}
                    onValueChange={handlePresetChange}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 16,
                        marginTop: 8,
                        maxHeight: 280,
                        width: "100%",
                        overflow: "auto",
                      }}
                    >
                      {(canvasRatio === "16:9"
                        ? landscapePresetsWithPreview
                        : canvasRatio === "9:16"
                        ? portraitPresetsWithPreview
                        : squarePresetsWithPreview
                      ).map((preset) => (
                        <ToggleGroup.Item
                          key={preset.key}
                          className="recordingLayoutsItem"
                          value={preset.key}
                          aria-label={preset.name}
                        >
                          <Flex
                            vertical
                            align="center"
                            justify="center"
                            gap={4}
                          >
                            <div
                              className="recordingLayoutsImg"
                              style={{
                                border:
                                  selectedPreset === preset.key
                                    ? "2px solid var(--dadan-primary)"
                                    : "2px solid transparent",
                              }}
                            >
                              <img
                                src={preset.preview}
                                alt={preset.name}
                                style={{
                                  width:
                                    canvasRatio === "16:9" ? "100%" : "auto",
                                  height:
                                    canvasRatio === "9:16" ? "100%" : "auto",
                                }}
                              />
                            </div>
                            <div>{preset.name}</div>
                          </Flex>
                        </ToggleGroup.Item>
                      ))}
                    </div>
                  </ToggleGroup.Root>
                </div>
              </div>
            }
          >
            <Tooltip title={isCompactMode ? "Layout" : undefined} placement="top">
              <Button
                type="text"
                size="large"
                style={{
                  ...ToolbarButtonStyle,
                  width: isCompactMode ? 'auto' : 80,
                }}
              >
                <Flex vertical gap={2} align="center">
                  <PanelsTopLeft size={24} />
                  {!isCompactMode && <>Layout</>}
                </Flex>
              </Button>
            </Tooltip>
          </Popover>

          {/* Teleprompter */}
          <CompactTooltip title="Prompter">
            <Button
              type="text"
              size="large"
              style={{
                ...ToolbarButtonStyle,
                width: isCompactMode ? 'auto' : 80,
              }}
              onClick={() => setIsTeleprompterOpen(true)}
            >
              <Flex vertical gap={2} align="center">
                <NotepadText size={24} />
                {!isCompactMode && <>Prompter</>}
              </Flex>
            </Button>
          </CompactTooltip>
        </Flex>
        <Divider type="vertical" style={{ height: 32 }} orientationMargin={0} />
        {/* Section 3 */}
        <Flex gap={12} align="center">
          {/* Settings - show for quick recording mode (blank mode) or when no active project */}
          {!isRecording && !showCountdown && (!activeProject || isBlankMode) && (
            <Popover
              trigger="click"
              placement="top"
              onOpenChange={(open) => setAnyPopoverOpen(open)}
              title={
                <Title
                  level={4}
                  style={{ marginBlockStart: 8, paddingInline: 16 }}
                >
                  Settings
                </Title>
              }
              content={
                <div
                  style={{
                    width: 280,
                    padding: "8px 16px 12px",
                  }}
                >
                  <div>
                    <Form
                      form={settingsForm}
                      layout="vertical"
                      initialValues={{
                        countdown: enableCountdown,
                      }}
                      style={{ width: "100%" }}
                    >
                      <Form.Item
                        label="Show Countdown"
                        name="countdown"
                        layout="horizontal"
                        labelAlign="left"
                        colon={false}
                        labelCol={{ span: 16 }}
                      >
                        <Switch
                          style={{
                            marginInlineStart: "auto",
                            display: "flex",
                          }}
                          checked={enableCountdown}
                          onChange={(checked) => setEnableCountdown(checked)}
                        />
                      </Form.Item>
                      <Form.Item
                        label="Show Guides"
                        name="guides"
                        layout="horizontal"
                        labelAlign="left"
                        colon={false}
                        labelCol={{ span: 16 }}
                      >
                        <Switch
                          style={{
                            marginInlineStart: "auto",
                            display: "flex",
                          }}
                          checked={showGrid}
                          onChange={(checked) => setShowGrid(checked)}
                        />
                      </Form.Item>
                      <Divider
                        style={{
                          marginBlock: "16px 12px",
                          borderBlockStart: "1px solid rgba(0,0,0,0.08)",
                        }}
                      />
                      {/* Phase 3: Device capability hint */}
                      {qualityConfig?.showHint && deviceTier && (
                        <DeviceCapabilityHint 
                          deviceTier={deviceTier}
                          visible={true}
                        />
                      )}
                      <Form.Item
                        label="Recording Quality"
                        name="quality"
                        layout="vertical"
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                      >
                        <Dropdown
                          menu={{
                            items: (() => {
                              // Phase 1: Free users are limited to 720p - use isPaid for reliable check
                              const isResolutionLimited = !isPaid;
                              
                              // Phase 1: Get quality items based on device + user permissions
                              const qualityItems = getQualityDropdownItems(
                                qualityConfig,
                                isResolutionLimited,
                                recordingQuality
                              );
                              
                              return qualityItems;
                            })(),
                            selectable: true,
                            selectedKeys: [recordingQuality],
                            onClick: ({ key }) => {
                              // Phase 1: Free users are limited to 720p
                              const isResolutionLimited = !isPaid;
                              
                              // Phase 1: Show upgrade prompt if quality is premium and user doesn't have access
                              if ((key === "1080p" || key === "2k") && isResolutionLimited) {
                                const qualityName = key === "1080p" ? "1080p Recording" : "2k Recording";
                                const description = key === "1080p" 
                                  ? "Upgrade to Pro to unlock high-definition 1080p recording for sharper, more professional videos."
                                  : "Upgrade to Pro to unlock ultra-high-definition 2k recording with enhanced detail for larger monitors.";
                                
                                setUpgradePromptFeature(qualityName);
                                setUpgradePromptDescription(description);
                                setShowUpgradePrompt(true);
                                return;
                              }
                              
                              const newQuality = key as import("../context/RecorderContext").RecordingQuality;
                              // Capture snapshot of current canvas with dimensions and offsets before resize
                              try {
                                const canvas = stageRef?.current?.getCanvas?.();
                                if (canvas) {
                                  const lowerCanvas = canvas.lowerCanvasEl;
                                  const canvasRect = lowerCanvas?.getBoundingClientRect();
                                  const containerRect = lowerCanvas?.parentElement?.getBoundingClientRect();
                                  const snapshot = canvas.toDataURL({ format: 'png', quality: 1 });
                                  store.set(canvasTransitionSnapshotAtom, {
                                    dataUrl: snapshot,
                                    width: canvasRect?.width || canvas.width,
                                    height: canvasRect?.height || canvas.height,
                                    offsetLeft: containerRect && canvasRect ? canvasRect.left - containerRect.left : 0,
                                    offsetTop: containerRect && canvasRect ? canvasRect.top - containerRect.top : 0,
                                  });
                                }
                              } catch (e) {
                                // Fallback: continue without snapshot
                              }
                              // Set transitioning flag before quality change to hide objects during resize
                              store.set(isRatioTransitioningAtom, true);
                              // Single source of truth: only update Context (not Jotai atom)
                              // RecorderContext syncs to Jotai atom if needed
                              setRecordingQualityContext(newQuality);
                            },
                          }}
                          trigger={["click"]}
                        >
                          <Button
                            type="default"
                            style={{ width: "100%", textAlign: "left" }}
                          >
                            <Space>
                              {recordingQuality === "720p" && "720p (HD)"}
                              {recordingQuality === "1080p" &&
                                "1080p (Full HD)"}
                              {recordingQuality === "2k" && "2k (Quad HD)"}
                            </Space>
                          </Button>
                        </Dropdown>
                      </Form.Item>
                    </Form>
                  </div>
                </div>
              }
            >
              <Button
                type="text"
                size="large"
                onClick={() => {}}
                style={{
                  ...ToolbarButtonStyle,
                  width: 90,
                }}
              >
                <Flex vertical gap={2} align="center">
                  <Settings size={24} />
                  <>Settings</>
                </Flex>
              </Button>
            </Popover>
          )}


          {/* Record/Retake Button */}
          {!isRecording ? (
            currentScene && (
              currentScene.videoUrl || 
              (currentScene.videoChunks && currentScene.videoChunks.length > 0) ||
              currentScene.status === 'pending_upload' || // Show Retake for pending scenes
              currentScene.status === 'pending_confirmation' || // Show Retake for unconfirmed recordings
              currentScene.status === 'recorded' // Show Retake for confirmed recordings
            ) ? (
              // Retake Button (when scene is recorded or pending upload)
              <Button
                variant="solid"
                color="primary"
                size="large"
                className="recordingButton"
                disabled={
                  (isPresentationImport ? (!isWebcamOn && !isScreenSharing && (!isMicStream || isMicrophoneMuted)) : (!isWebcamOn && !isScreenSharing)) ||
                  showCountdown ||
                  (isUploading && !isRecording) ||
                  showRetryButton
                  // Note: showStuckChunksOverlay removed - scene-based projects use pending state system
                }
                onClick={() => setIsRetakeModalOpen(true)}
              >
                <Flex align="center" gap={8}>
                  <span className="retakeIcon">
                    <RotateCcw size={16} />
                  </span>
                  Retake
                </Flex>
              </Button>
            ) : (
              // Record Button (when scene is not recorded)
              <Button
                variant="solid"
                color="primary"
                size="large"
                className="recordingButton"
                disabled={
                  (isPresentationImport ? (!isWebcamOn && !isScreenSharing && (!isMicStream || isMicrophoneMuted)) : (!isWebcamOn && !isScreenSharing)) ||
                  showCountdown ||
                  (isUploading && !isRecording) ||
                  showRetryButton
                  // Note: showStuckChunksOverlay removed - scene-based projects use pending state system
                }
                onClick={() => {
                  // Check if project-based recording limit is exhausted (cumulative across scenes)
                  if (!isBlankMode && isLimitExhausted) {
                    const limitMinutes = totalLimitSeconds ? Math.round(totalLimitSeconds / 60) : 5;
                    setUpgradePromptFeature("Extended Recording Time");
                    setUpgradePromptDescription(
                      `You've used all ${limitMinutes} minutes of your recording limit across this project. Upgrade to Pro for unlimited recording duration.`
                    );
                    setShowUpgradePrompt(true);
                    return;
                  }
                  handleStart();
                }}
              >
                <Flex align="center" gap={8}>
                  <span className="recordingDot" />
                  Record
                </Flex>
              </Button>
            )
          ) : (
            <Button
              variant="solid"
              color="danger"
              size="large"
              className={`recordingButton ${
                !isPaused ? "recordingButtonActive" : ""
              }`}
              onMouseEnter={() => {
                setIsRecordingButtonHovered(true);
              }}
              onMouseLeave={() => {
                setIsRecordingButtonHovered(false);
              }}
              onClick={stopRecording}
            >
              <Flex align="center" gap={8}>
                <span className="recordingDot" />
                {isRecordingButtonHovered ? (
                  "Stop"
                ) : (
                  <span className="recordingTime">
                    {isLimited && remainingSeconds !== null ? formatTime(remainingSeconds) : currentTimerValueRef.current}
                  </span>
                )}
              </Flex>
            </Button>
          )}

          {/* Pause/Resume Button */}
          {isRecording && (
            <Button
              color="primary"
              variant="filled"
              size="large"
              icon={
                isPaused ? (
                  <Play size={20} fill="var(--dadan-primary)" strokeWidth={0} />
                ) : (
                  <Pause
                    size={20}
                    fill="var(--dadan-primary)"
                    strokeWidth={0}
                  />
                )
              }
              onClick={isPaused ? resumeRecording : pauseRecording}
            ></Button>
          )}

          {/* Delete Recording */}
          {isRecording && (
            <Button
              type="text"
              size="large"
              icon={<Trash2 size={24} />}
              onClick={() => {
                // Pause recording first, then show delete modal
                pauseRecording();
                setIsDeleteModalOpen(true);
              }}
            />
          )}
          {!detectBrowser().isSafari && showPipButton && (
            <CompactTooltip title="Picture in Picture">
              <Button
                type="text"
                size="large"
                className="pipButton"
                disabled={(!isWebcamOn && !isScreenSharing) || isPipOpen}
                onClick={handleOpenPiP}
                style={{
                  ...ToolbarButtonStyle,
                  width: isCompactMode ? 'auto' : 80,
                }}
              >
                <Flex vertical gap={isCompactMode ? 0 : 2} align="center">
                  <PictureInPicture size={isCompactMode ? 20 : 24} />
                  {!isCompactMode && <>PIP</>}
                </Flex>
              </Button>
            </CompactTooltip>
          )}
        </Flex>
      </Flex>
      </div>
      <Modal
        title={getModalContent().title}
        centered
        maskClosable={false}
        open={isModalStreamOpen}
        afterOpenChange={() => {
          setIsLayoutPopoverOpen(false);
          setAnyPopoverOpen(false);
        }}
        onOk={() => {
          setIsModalStreamOpen(false);
        }}
        onCancel={() => {
          setIsModalStreamOpen(false);
          setPendingPreset(null);
          
          // Apply WebcamFull fallback preset when user cancels (session-only, not persisted)
          // This ensures the canvas layout is clean when user declines to share streams
          if (webcamStream) {
            // User has webcam, apply WebcamFull as fallback
            const fallbackRatio = 16 / 9;
            applyPreset(
              Preset_WebcamFull,
              fallbackRatio,
              canvasWidth,
              canvasHeight,
              canvasRatio,
              roundedWebcam,
              false
            );
            setSelectedPreset("Preset_WebcamFull");
          } else {
            // No streams at all, clear layout
            setInitialStreamLayout([]);
            setSelectedPreset("Preset_Free");
          }
        }}
        footer={null}
        zIndex={1005}
      >
        <p>{getModalContent().body}</p>

        <Flex gap={12} style={{ marginTop: 16 }}>
          {modalNeeds.webcam && (
            <Button
              type="primary"
              disabled={isWebcamOn}
              onClick={async () => {
                if (!pendingPreset?.preset) return;

                // Stop screen if it's not needed
                if (!pendingPreset.requiresScreen && isScreenSharing) {
                  await handleStopScreenShare();
                }

                handleStartWebcam();
              }}
            >
              Start Webcam
            </Button>
          )}

          {modalNeeds.screen && (
            <Button
              type="primary"
              disabled={isScreenSharing}
              onClick={async () => {
                if (!pendingPreset?.preset) return;
                // Stop webcam if it's not needed
                if (!pendingPreset.requiresWebcam && webcamStream) {
                  await handleStopWebcam();
                }

                handleStartScreenShare();
              }}
            >
              Share Screen
            </Button>
          )}
        </Flex>
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete this recording?"
        centered
        maskClosable={false}
        open={isDeleteModalOpen}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          // Resume recording after a small delay
          setTimeout(() => {
            resumeRecording();
          }, 500);
        }}
        footer={null}
        zIndex={1005}
      >
        <p>
          This will permanently remove the current take. You can’t undo this.
        </p>

        <Flex gap={12} style={{ marginTop: 24 }}>
          {/* Delete Button */}
          <Button
            type="primary"
            danger
            onClick={async () => {
              setIsDeleteModalOpen(false);
              await deleteRecording();
            }}
          >
            Delete Recording
          </Button>

          {/* Delete and Start Over Button */}
          <Button
            variant="filled"
            color="primary"
            onClick={async () => {
              setIsDeleteModalOpen(false);
              await deleteRecording();
              // Use direct startRecording instead of handleStart to avoid countdown issues
              // Wait a bit longer to ensure worker is fully recreated
              setTimeout(() => {
                handleStart(true);
              }, 200);
            }}
          >
            Start Over
          </Button>

          {/* Continue Recording Button */}
          <Button
            type="default"
            onClick={() => {
              setIsDeleteModalOpen(false);
              // Resume recording after a small delay
              setTimeout(() => {
                resumeRecording();
              }, 500);
            }}
          >
            Keep Recording
          </Button>
        </Flex>
      </Modal>
      
      {/* Retake Confirmation Modal */}
      <Modal
        title="Retake Scene"
        centered
        maskClosable={false}
        open={isRetakeModalOpen}
        onCancel={() => setIsRetakeModalOpen(false)}
        footer={null}
        zIndex={1005}
      >
        <div>
          <p>Are you sure you want to retake <strong>{currentScene?.title}</strong>?</p>
          
          <Alert
            message="The current recording will be replaced with a new one."
            type="warning"
            showIcon
            style={{ marginTop: 16, marginBottom: 8 }}
          />
          
          <p style={{ marginTop: 8, color: '#6b7280' }}>
            You can review the new recording before saving.
          </p>
        </div>

        <Flex gap={12} style={{ marginTop: 24 }}>
          <Button
            type="primary"
            style={{ background: '#f59e0b', borderColor: '#f59e0b' }}
            onClick={async () => {
              // Check if project-based recording limit is exhausted (cumulative across scenes)
              if (!isBlankMode && isLimitExhausted) {
                setIsRetakeModalOpen(false);
                const limitMinutes = totalLimitSeconds ? Math.round(totalLimitSeconds / 60) : 5;
                setUpgradePromptFeature("Extended Recording Time");
                setUpgradePromptDescription(
                  `You've used all ${limitMinutes} minutes of your recording limit across this project. Upgrade to Pro for unlimited recording duration.`
                );
                setShowUpgradePrompt(true);
                return;
              }
              
              setIsRetakeModalOpen(false);
              
              // Handle scene retakes: reset thumbnail, duration, and status
              if (currentScene) {
                try {
                  const { db, deleteAllPendingRecordingsForScene } = await import('../idb/chunk-db');
                  const SceneService = (await import('../services/sceneService')).default;
                  
                  // Delete ALL IndexedDB chunks for this scene (by sceneId AND videoGuid)
                  // This ensures we remove both current chunks and any orphaned chunks
                  const chunksBySceneId = await db.chunks
                    .where('sceneId')
                    .equals(currentScene.id)
                    .toArray();
                  
                  for (const chunk of chunksBySceneId) {
                    await db.chunks.delete(chunk.id);
                  }
                  
                  logger.info('[Retake] Deleted ALL IndexedDB chunks for scene:', {
                    sceneId: currentScene.id,
                    chunksDeleted: chunksBySceneId.length,
                    videoGuids: [...new Set(chunksBySceneId.map(c => c.videoGuid))]
                  });
                  
                  // Delete pending separate stream recordings from IndexedDB
                  try {
                    await deleteAllPendingRecordingsForScene(String(currentScene.id));
                    logger.info('[Retake] Deleted pending recordings from IndexedDB for scene:', currentScene.id);
                  } catch (pendingError) {
                    logger.warn('[Retake] Failed to delete pending recordings:', pendingError);
                  }
                  
                  // Reset scene: clear thumbnail, duration, videoUrl for ALL scene types
                  // (both 'pending_upload' and 'recorded' scenes need this)
                  await SceneService.updateScene(
                    currentScene.id, 
                    {
                      status: 'pending',
                      duration: null,
                      videoUrl: null,
                      thumbnailUrl: null,
                      videoGuid: null,
                      analysisStatus: 'pending', // Reset analysis status for re-recording
                    },
                    currentScene.projectId
                  );
                  
                  // Create updated scene object for atoms
                  const updatedScene = {
                    ...currentScene,
                    status: 'pending' as const,
                    duration: 0,
                    videoUrl: undefined,
                    thumbnailUrl: undefined,
                    videoGuid: undefined,
                    videoChunks: [],
                    analysisStatus: 'pending' as const, // Reset analysis status for re-recording
                  };
                  
                  // Update currentScene atom to reflect the reset
                  setCurrentScene(updatedScene);
                  
                  // Update sceneList atom to sync the change
                  setSceneList((prevList) =>
                    prevList.map((scene) =>
                      scene.id === updatedScene.id ? updatedScene : scene
                    )
                  );
                  
                  logger.info('[Retake] Scene reset complete - thumbnail and duration cleared');
                  
                  // Start a new recording after resetting the scene
                  setTimeout(() => {
                    handleStart(true);
                  }, 200);
                } catch (error) {
                  logger.error('[Retake] Failed to reset scene:', error);
                }
              } else {
                // For non-scene recordings, use the standard flow
                await deleteRecording();
                setTimeout(() => {
                  handleStart(true);
                }, 200);
              }
            }}
          >
            Retake
          </Button>
          <Button
            type="default"
            onClick={() => setIsRetakeModalOpen(false)}
          >
            Cancel
          </Button>
        </Flex>
      </Modal>
      
      {/* Phase 1: Upgrade Prompt Modal */}
      <UpgradePrompt
        visible={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        feature={upgradePromptFeature}
        description={upgradePromptDescription}
      />
      
      <TeleprompterModal
        open={isTeleprompterOpen}
        isRecording={isRecording}
        currentSceneScript={isBlankMode ? '' : (currentScene?.script || '')}
        onClose={() => setIsTeleprompterOpen(false)}
        onTeleprompterStateChange={(state) => {
          setTeleprompterText(state.text);
          setCurrentWordIndex(state.currentWordIndex);
          setIsPlaying(state.isPlaying);
          setHighlightSpeed(state.highlightSpeed);
        }}
        onScriptGenerated={async (generatedScript: string) => {
          console.log('[RecordingToolbar] onScriptGenerated callback triggered', {
            hasCurrentScene: !!currentScene,
            currentSceneId: currentScene?.id,
            hasActiveProject: !!activeProject,
            activeProjectId: activeProject?.id,
            scriptLength: generatedScript.length,
          });
          
          if (currentScene) {
            const updatedScene = {
              ...currentScene,
              script: generatedScript,
            };
            
            console.log('[RecordingToolbar] Updating scene in list:', {
              sceneId: currentScene.id,
              newScript: generatedScript.substring(0, 100),
            });
            
            updateSceneInList(updatedScene);
            
            if (activeProject?.id) {
              console.log('[RecordingToolbar] Calling SceneService.updateScene');
              
              try {
                await SceneService.updateScene(
                  String(currentScene.id),
                  { script: generatedScript },
                  String(activeProject.id)
                );
                console.log('[RecordingToolbar] ✅ Scene script synced successfully to database');
                logger.info('[Teleprompter] Scene script synced to database:', {
                  sceneId: currentScene.id,
                  projectId: activeProject.id,
                  scriptLength: generatedScript.length,
                });
              } catch (error) {
                console.error('[RecordingToolbar] ❌ Failed to sync script to database:', error);
              }
            } else {
              console.warn('[RecordingToolbar] No active project, skipping database sync');
            }
          } else {
            console.warn('[RecordingToolbar] No current scene, cannot sync script');
          }
        }}
      />
    </>
  );
});

export default RecordingToolbarBottom;
